import React, { useRef, useState, useEffect } from "react";

type AspectKey = "16:9" | "1:1";
const ASPECTS: Record<AspectKey, number> = { "16:9": 16 / 9, "1:1": 1 };
const OUTPUT_SIZES: Record<AspectKey, { w: number; h: number }> = {
  "16:9": { w: 1200, h: 675 },
  "1:1": { w: 675, h: 675 },
};

interface Props {
  src: string;
  onSave: (blob: Blob, aspect: AspectKey) => void;
  onClose: () => void;
}

interface Box { x: number; y: number; w: number; h: number }

const ImageCropper: React.FC<Props> = ({ src, onSave, onClose }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [aspect, setAspect] = useState<AspectKey>("16:9");
  const [crop, setCrop] = useState<Box>({ x: 0, y: 0, w: 0, h: 0 });
  const [imgRect, setImgRect] = useState({ w: 0, h: 0 });
  const [saving, setSaving] = useState(false);
  const dragRef = useRef<{ type: "move" | "resize"; sx: number; sy: number; sc: Box } | null>(null);

  const fitCrop = (iw: number, ih: number, asp: AspectKey) => {
    const ratio = ASPECTS[asp];
    let cw: number, ch: number;
    if (iw / ih > ratio) { ch = ih; cw = ch * ratio; }
    else { cw = iw; ch = cw / ratio; }
    setCrop({ x: (iw - cw) / 2, y: (ih - ch) / 2, w: cw, h: ch });
  };

  const onImgLoad = () => {
    const img = imgRef.current;
    if (!img) return;
    const r = img.getBoundingClientRect();
    setImgRect({ w: r.width, h: r.height });
    fitCrop(r.width, r.height, aspect);
  };

  useEffect(() => {
    if (imgRect.w > 0) fitCrop(imgRect.w, imgRect.h, aspect);
  }, [aspect]);

  const clamp = (c: Box): Box => ({
    x: Math.max(0, Math.min(c.x, imgRect.w - c.w)),
    y: Math.max(0, Math.min(c.y, imgRect.h - c.h)),
    w: Math.min(c.w, imgRect.w),
    h: Math.min(c.h, imgRect.h),
  });

  const onMouseDown = (e: React.MouseEvent, type: "move" | "resize") => {
    e.preventDefault();
    dragRef.current = { type, sx: e.clientX, sy: e.clientY, sc: { ...crop } };

    const ratio = ASPECTS[aspect];
    const onMove = (ev: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dx = ev.clientX - d.sx;
      const dy = ev.clientY - d.sy;
      if (d.type === "move") {
        setCrop(clamp({ ...d.sc, x: d.sc.x + dx, y: d.sc.y + dy }));
      } else {
        const nw = Math.max(60, Math.min(d.sc.w + dx, imgRect.w - d.sc.x));
        const nh = nw / ratio;
        if (d.sc.y + nh <= imgRect.h) {
          setCrop({ x: d.sc.x, y: d.sc.y, w: nw, h: nh });
        }
      }
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleSave = async () => {
    const img = imgRef.current;
    if (!img || saving) return;
    setSaving(true);
    try {
      const nat = { w: img.naturalWidth, h: img.naturalHeight };
      const scaleX = nat.w / imgRect.w;
      const scaleY = nat.h / imgRect.h;
      const out = OUTPUT_SIZES[aspect];
      const canvas = document.createElement("canvas");
      canvas.width = out.w;
      canvas.height = out.h;
      const ctx = canvas.getContext("2d")!;
      ctx.drawImage(
        img,
        crop.x * scaleX, crop.y * scaleY, crop.w * scaleX, crop.h * scaleY,
        0, 0, out.w, out.h
      );
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.9)
      );
      if (blob) await onSave(blob, aspect);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="cropper-overlay" onClick={onClose}>
      <div className="cropper-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cropper-header">
          <div className="cropper-aspects">
            {(Object.keys(ASPECTS) as AspectKey[]).map((a) => (
              <button
                key={a}
                className={`cropper-aspect-btn ${aspect === a ? "cropper-aspect-btn--active" : ""}`}
                onClick={() => setAspect(a)}
              >
                {a}
              </button>
            ))}
          </div>
          <div className="cropper-actions">
            <button className="cropper-cancel" onClick={onClose}>Cancel</button>
            <button className="cropper-save" onClick={handleSave} disabled={saving}>
              {saving ? (
                <><span className="cropper-spinner" /> Saving...</>
              ) : "Save crop"}
            </button>
          </div>
        </div>
        <div className="cropper-canvas-wrap" ref={wrapRef}>
          <div className="cropper-img-container" style={{ position: "relative", display: "inline-block" }}>
            <img
              ref={imgRef}
              src={src}
              crossOrigin="anonymous"
              onLoad={onImgLoad}
              style={{ display: "block", maxWidth: "85vw", maxHeight: "75vh" }}
              draggable={false}
            />
            {imgRect.w > 0 && (
              <>
                {/* darkened overlay — 4 rects around crop */}
                <div style={{ position: "absolute", top: 0, left: 0, width: "100%", height: crop.y, background: "rgba(0,0,0,0.4)" }} />
                <div style={{ position: "absolute", top: crop.y, left: 0, width: crop.x, height: crop.h, background: "rgba(0,0,0,0.4)" }} />
                <div style={{ position: "absolute", top: crop.y, left: crop.x + crop.w, right: 0, height: crop.h, background: "rgba(0,0,0,0.4)" }} />
                <div style={{ position: "absolute", top: crop.y + crop.h, left: 0, width: "100%", bottom: 0, background: "rgba(0,0,0,0.4)" }} />
                {/* crop area — draggable */}
                <div
                  style={{
                    position: "absolute", top: crop.y, left: crop.x, width: crop.w, height: crop.h,
                    border: "2px solid #fff", boxSizing: "border-box", cursor: "grab",
                  }}
                  onMouseDown={(e) => onMouseDown(e, "move")}
                >
                  {/* grid */}
                  <div style={{ position: "absolute", left: "33.33%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.3)" }} />
                  <div style={{ position: "absolute", left: "66.66%", top: 0, bottom: 0, width: 1, background: "rgba(255,255,255,0.3)" }} />
                  <div style={{ position: "absolute", top: "33.33%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.3)" }} />
                  <div style={{ position: "absolute", top: "66.66%", left: 0, right: 0, height: 1, background: "rgba(255,255,255,0.3)" }} />
                </div>
                {/* resize handle */}
                <div
                  style={{
                    position: "absolute",
                    top: crop.y + crop.h - 12, left: crop.x + crop.w - 12,
                    width: 16, height: 16, cursor: "nwse-resize",
                    background: "#fff", borderRadius: 2,
                  }}
                  onMouseDown={(e) => onMouseDown(e, "resize")}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
